import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import {
  trackCTA,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { WSE_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * The plan qualifier — the most important non-negotiable on this page.
 *
 * Everything the page describes is Heirloom-only, and not partially. Three
 * independent gates say so:
 *   - `PLAN_FEATURE_MATRIX.invitationProduct` is true for `pro` alone
 *     (api/constants/planFeatures.mjs), and its comment names seating in the
 *     same entitlement as the invitation, RSVP, guest list and Planning Hub;
 *   - the whole couple-facing seating router is mounted behind
 *     `requireProPlanWithCode` (api/routes/seating.routes.mjs), as is the
 *     guest-facing seat lookup (api/routes/seatingLookup.routes.mjs);
 *   - the app hides the Seating nav item outright for any other plan
 *     (app/src/routes/wedding-sitemap.ts) and renders a `ProPlanCTA` if the
 *     route is reached directly.
 *
 * A Blossom or Bouquet couple has no seating at all — not a smaller one — so a
 * page about seating that leaves that ambiguous is a page that mis-sells.
 * Three statements in order, so a reader who takes only one of them still
 * cannot come away with the wrong idea:
 *
 *   1. seating and everything it reads from are included with Heirloom,
 *   2. photo sharing is on every plan and seating is NOT,
 *   3. why that follows — with a link to the plans.
 *
 * Named as a section (`heading-50`, its own impression) rather than a footnote,
 * because a plan qualifier that reads as fine print is one someone can honestly
 * say they missed.
 *
 * It is also where the page hands on to its four siblings. Every link is
 * contextual rather than decorative: this is the one section that talks about
 * what a plan contains rather than about what seating does, and the order is
 * the couple's own journey — the RSVP that fills the board comes first, the
 * planning space it feeds next, then the invitation the replies arrive in, and
 * last the gallery that outlives all of it.
 */
const SeatingPlan = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.PLAN, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">{t("weddingSeating.plan.heading")}</h2>
            <p className="body-20">{t("weddingSeating.plan.body1")}</p>
            <p className="body-20">{t("weddingSeating.plan.body2")}</p>
            <p className="body-20">{t("weddingSeating.plan.body3")}</p>
          </div>
        </div>

        <p className="wps-plan-note">
          {t("weddingSeating.plan.note")}{" "}
          <a className="wps-text-link" href={localize("/#pricing")} onClick={scrollToPlans}>
            {t("weddingSeating.plan.noteLink")}
          </a>
        </p>

        <div className="wpl-cross-links">
          <a
            className="wps-text-link"
            href={localize("/wedding-rsvp")}
            onClick={() =>
              trackCTA("wedding_seating_plan", {
                ...analyticsContext,
                ctaName: "wedding_rsvp",
              })
            }
          >
            {t("weddingSeating.plan.rsvpLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-planning")}
            onClick={() =>
              trackCTA("wedding_seating_plan", {
                ...analyticsContext,
                ctaName: "wedding_planning",
              })
            }
          >
            {t("weddingSeating.plan.planningLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/digital-wedding-invitation")}
            onClick={() =>
              trackCTA("wedding_seating_plan", {
                ...analyticsContext,
                ctaName: "digital_wedding_invitation",
              })
            }
          >
            {t("weddingSeating.plan.invitationLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-photo-sharing")}
            onClick={() =>
              trackCTA("wedding_seating_plan", {
                ...analyticsContext,
                ctaName: "wedding_photo_sharing",
              })
            }
          >
            {t("weddingSeating.plan.photoLink")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default SeatingPlan;
